// /admin/presupuestos/:id — detalle de un presupuesto.
// Acciones: descargar HTML (vía edge function que devuelve signed URL),
// marcar enviado/aceptado/rechazado/vencido, duplicar, ver items, ir a la empresa.

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const ESTADO_LABEL = {
  borrador:  { l: 'Borrador',  bg: '#F1F5F9', fg: '#64748B', hint: 'Trabajando en él, no se mandó' },
  enviado:   { l: 'Enviado',   bg: '#E0F2FE', fg: '#0EA5E9', hint: 'Mandado al cliente, esperando respuesta' },
  aceptado:  { l: 'Aceptado',  bg: '#DCFCE7', fg: '#16A34A', hint: 'Cliente confirmó' },
  rechazado: { l: 'Rechazado', bg: '#FEE2E2', fg: '#DC2626', hint: 'Cliente dijo que no' },
  vencido:   { l: 'Vencido',   bg: '#FEF3C7', fg: '#CA8A04', hint: 'Pasó la validez sin respuesta' },
}

export default function PresupuestoDetalle() {
  const { id } = useParams()
  const nav = useNavigate()
  const [presupuesto, setPresupuesto] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [accion, setAccion] = useState(null) // 'enviar' | 'aceptar' | 'rechazar' | 'descargar' | null
  const [info, setInfo] = useState(null)

  const [generados, setGenerados] = useState({ suscripciones: [], pedidos: [] })

  async function cargar() {
    setCargando(true)
    const [{ data, error: e }, { data: subs }, { data: pds }] = await Promise.all([
      supabase.from('presupuestos').select(`
        *,
        empresa:empresas(id, razon_social, rut, tipo, telefono, email_facturacion, contacto_principal_nombre),
        items:presupuestos_items(*, producto:productos(nombre, marca, presentacion), kit:kits(nombre)),
        creador:profiles!presupuestos_created_by_fkey(id, nombre)
      `).eq('id', id).single(),
      supabase.from('suscripciones_empresa').select('id, alias, estado, precio_mensual_total, fecha_inicio').eq('presupuesto_origen_id', id),
      supabase.from('pedidos').select('id, numero_orden, kit_codigo, monto_total, estado, created_at').eq('presupuesto_id', id),
    ])
    if (e) setError(e.message)
    else setPresupuesto(data)
    setGenerados({ suscripciones: subs || [], pedidos: pds || [] })
    setCargando(false)
  }
  useEffect(() => { cargar() }, [id])

  // Pide el HTML a la edge function y lo abre como Blob URL local
  // (workaround del comportamiento de Supabase Storage que sirve HTML
  // privado como text/plain — un Blob URL local sí lo renderiza como HTML real).
  async function _abrirHtml(autoPrint) {
    const { data, error: e } = await supabase.functions.invoke('generar-presupuesto-html', {
      body: { presupuesto_id: id, print: !!autoPrint },
    })
    if (e) throw new Error(e.message || 'No se pudo generar el HTML')
    if (!data?.html) throw new Error('La función no devolvió HTML')
    const blob = new Blob([data.html], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const win = window.open(url, '_blank', 'noopener,noreferrer')
    if (!win) {
      throw new Error('El navegador bloqueó la pestaña. Permitir pop-ups para panel.limpack.uy.')
    }
    // Liberar la URL después de un rato (la pestaña ya cargó el contenido)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  async function abrirHtml() {
    setAccion('descargar')
    setInfo(null)
    setError(null)
    try {
      await _abrirHtml(false)
      setInfo('HTML abierto en nueva pestaña. Revisalo antes de mandarlo al cliente.')
    } catch (err) {
      setError(err.message)
    } finally {
      setAccion(null)
    }
  }

  async function imprimirPdf() {
    setAccion('imprimir')
    setInfo(null)
    setError(null)
    try {
      await _abrirHtml(true)
      setInfo('Pestaña abierta con dialog de impresión. Destino "Guardar como PDF" → adjuntar al mail.')
    } catch (err) {
      setError(err.message)
    } finally {
      setAccion(null)
    }
  }

  const [modalEmail, setModalEmail] = useState(false)

  async function cambiarEstado(nuevoEstado, extra = {}) {
    setAccion(nuevoEstado)
    setError(null)
    setInfo(null)
    const patch = { estado: nuevoEstado, ...extra }
    if (nuevoEstado === 'enviado' && !presupuesto.enviado_at) {
      patch.enviado_at = new Date().toISOString()
    }
    if (nuevoEstado === 'aceptado' && !presupuesto.aceptado_at) {
      patch.aceptado_at = new Date().toISOString()
    }
    if (nuevoEstado === 'rechazado' && !presupuesto.rechazado_at) {
      patch.rechazado_at = new Date().toISOString()
    }
    const { error: e } = await supabase.from('presupuestos').update(patch).eq('id', id)
    if (e) setError(e.message)
    else {
      setInfo(`Estado actualizado a "${ESTADO_LABEL[nuevoEstado]?.l || nuevoEstado}"`)
      cargar()
    }
    setAccion(null)
  }

  async function marcarEnviado() {
    const email = prompt('¿A qué email lo mandaste?', presupuesto.empresa?.email_facturacion || '')
    if (email === null) return
    await cambiarEstado('enviado', { enviado_a_email: email || null })
  }

  async function marcarAceptado() {
    const quien = prompt('¿Quién confirmó del lado del cliente?', presupuesto.empresa?.contacto_principal_nombre || '')
    if (quien === null) return
    await cambiarEstado('aceptado', { aceptado_por: quien || null })
  }

  async function marcarRechazado() {
    const motivo = prompt('Motivo del rechazo (opcional)', '')
    if (motivo === null) return
    await cambiarEstado('rechazado', { rechazado_motivo: motivo || null })
  }

  // Duplicar presupuesto: crea uno nuevo con nuevo número + copia items.
  // El original queda como está (para trazabilidad). Nuevo estado: borrador.
  async function duplicar() {
    if (!confirm(`¿Duplicar presupuesto ${presupuesto.numero}?\n\nSe crea uno nuevo con nuevo número, copiando todos los items y condiciones. El original queda intacto.`)) return
    setAccion('duplicar')
    setError(null)
    setInfo(null)
    try {
      const { data: nuevoId, error: e } = await supabase.rpc('duplicar_presupuesto', { p_id: id })
      if (e) throw e
      if (!nuevoId) throw new Error('La función no devolvió el ID nuevo')
      // Redirigir al detalle del nuevo — desde ahí Nico puede editar cambios
      // (ideal: agregar modo edición al wizard, pero por ahora ir al detalle)
      nav(`/admin/presupuestos/${nuevoId}`)
    } catch (e) {
      setError('Error duplicando: ' + (e?.message || String(e)))
    } finally {
      setAccion(null)
    }
  }

  if (cargando) return <AdminLayout title="Presupuesto"><div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div></AdminLayout>
  if (error && !presupuesto) return <AdminLayout title="Presupuesto"><div style={{ padding: 24, color: '#991B1B' }}>{error}</div></AdminLayout>
  if (!presupuesto) return <AdminLayout title="Presupuesto"><div style={{ padding: 24 }}>No encontrado</div></AdminLayout>

  const est = ESTADO_LABEL[presupuesto.estado] || ESTADO_LABEL.borrador
  const items = (presupuesto.items || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))

  return (
    <AdminLayout title={`Presupuesto ${presupuesto.numero}`}>
      <Estilos />
      <div className="pd-page">
        <button className="pd-back" onClick={() => nav(`/admin/empresas/${presupuesto.empresa.id}`)}>
          ← Volver a {presupuesto.empresa.razon_social}
        </button>

        <header className="pd-head">
          <div>
            <div className="pd-numero">
              Presupuesto Nº <strong>{presupuesto.numero}</strong>
              <span className="pd-modalidad">
                {presupuesto.modalidad === 'compra_unica' ? '🛍 Compra única' : '🔄 Servicio mensual'}
              </span>
            </div>
            <h1>{presupuesto.empresa.razon_social}</h1>
            <div className="pd-meta">
              Emitido {formatFecha(presupuesto.fecha)} · Válido {presupuesto.validez_dias} días
              {presupuesto.creador?.nombre && ` · Por ${presupuesto.creador.nombre}`}
            </div>
          </div>
          <span className="pd-badge" style={{ background: est.bg, color: est.fg }} title={est.hint}>{est.l}</span>
        </header>

        {info && <div className="pd-info">{info}</div>}
        {error && <div className="pd-error">⚠ {error}</div>}

        <div className="pd-actions-bar">
          <button className="pd-btn-primary" onClick={imprimirPdf} disabled={accion === 'imprimir'}>
            {accion === 'imprimir' ? 'Abriendo…' : '📥 Imprimir / Guardar PDF'}
          </button>
          <button className="pd-btn-secondary" onClick={abrirHtml} disabled={accion === 'descargar'}>
            {accion === 'descargar' ? 'Abriendo…' : '👁 Vista previa HTML'}
          </button>
          <button className="pd-btn-secondary" onClick={() => setModalEmail(true)} disabled={!!accion}>
            📧 Preparar email
          </button>
          {presupuesto.estado === 'borrador' && (
            <button
              className="pd-btn-secondary"
              onClick={() => nav(`/admin/empresas/${presupuesto.empresa.id}/presupuesto/nuevo?edit=${presupuesto.id}`)}
              disabled={!!accion}
              title="Editar este presupuesto (solo disponible en borrador). Mantiene el mismo número."
            >
              ✏️ Editar
            </button>
          )}
          <button className="pd-btn-secondary" onClick={duplicar} disabled={!!accion} title="Crea un presupuesto nuevo con nuevo número, copiando items y condiciones">
            📋 Duplicar
          </button>
          {presupuesto.estado === 'borrador' && (
            <button className="pd-btn-secondary" onClick={marcarEnviado} disabled={!!accion}>📤 Marcar enviado</button>
          )}
          {['borrador', 'enviado'].includes(presupuesto.estado) && (
            <>
              <button className="pd-btn-success" onClick={marcarAceptado} disabled={!!accion}>✓ Marcar aceptado</button>
              <button className="pd-btn-danger" onClick={marcarRechazado} disabled={!!accion}>✕ Marcar rechazado</button>
            </>
          )}
          {presupuesto.estado === 'aceptado' && presupuesto.modalidad === 'suscripcion' && (
            <button
              className="pd-btn-success"
              onClick={() => nav(`/admin/empresas/${presupuesto.empresa.id}/crear-suscripciones?presupuesto_id=${presupuesto.id}`)}
              title="Activa el servicio mensual con los items y precios de este presupuesto"
            >
              ➕ Activar suscripción B2B
            </button>
          )}
          {presupuesto.estado === 'aceptado' && presupuesto.modalidad === 'compra_unica' && (
            <button
              className="pd-btn-success"
              onClick={() => nav(`/admin/empresas/${presupuesto.empresa.id}/crear-pedido?presupuesto_id=${presupuesto.id}`)}
              title="Crea un pedido one-shot con los items y precios de este presupuesto"
            >
              ➕ Crear pedido B2B
            </button>
          )}
        </div>

        {modalEmail && (
          <ModalEmail
            presupuesto={presupuesto}
            onClose={() => setModalEmail(false)}
            onMarcadoEnviado={() => { setModalEmail(false); cargar() }}
          />
        )}

        <div className="pd-grid">
          <section className="pd-card">
            <h3>Items ({items.length})</h3>
            <table className="pd-items">
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th>Cant.</th>
                  <th>P. unit. s/IVA</th>
                  <th>Subtotal s/IVA</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  let desc = ''
                  if (it.tipo === 'producto' && it.producto) {
                    desc = [it.producto.marca, it.producto.nombre, it.producto.presentacion].filter(Boolean).join(' · ')
                  } else if (it.tipo === 'kit' && it.kit) {
                    desc = `Kit ${it.kit.nombre}`
                  } else {
                    desc = it.descripcion || ''
                  }
                  const subtotalL = Number(it.subtotal) || 0
                  const ivaPctL = Number(it.iva_pct) || 22
                  const cantL = Number(it.cantidad) || 1
                  const subSinIvaL = subtotalL / (1 + ivaPctL / 100)
                  const punitSinIvaL = cantL ? subSinIvaL / cantL : subSinIvaL
                  return (
                    <tr key={it.id}>
                      <td>{desc}</td>
                      <td className="pd-num">{cantL}</td>
                      <td className="pd-num">${punitSinIvaL.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</td>
                      <td className="pd-num"><strong>${subSinIvaL.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div style={{ textAlign: 'right', fontSize: 11, color: '#64748B', margin: '4px 0 10px' }}>Precios por línea sin IVA · IVA y total en el resumen</div>

            {presupuesto.ocultar_total ? (
              <div className="pd-totales" style={{ textAlign: 'left', background: '#F0FDFA', color: '#134E4A' }}>
                Lista de precios · sin total (precios por unidad)
              </div>
            ) : (
              <div className="pd-totales">
                <div>Subtotal sin IVA <strong>${(Number(presupuesto.total) - Number(presupuesto.iva_total)).toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></div>
                {Number(presupuesto.descuento_pct) > 0 && (
                  <div>Desc. global ({presupuesto.descuento_pct}%)</div>
                )}
                <div>IVA <strong>${Number(presupuesto.iva_total).toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></div>
                <div className="pd-total">Total (IVA inc.): <strong>${Number(presupuesto.total).toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></div>
              </div>
            )}
          </section>

          <aside className="pd-aside">
            <section className="pd-card">
              <h3>Condiciones</h3>
              <Field label="Pago"><div>{presupuesto.condiciones_pago || '—'}</div></Field>
              <Field label="Entrega"><div>{presupuesto.condiciones_entrega || '—'}</div></Field>
            </section>

            {presupuesto.notas_cliente && (
              <section className="pd-card">
                <h3>Notas al cliente</h3>
                <p className="pd-text">{presupuesto.notas_cliente}</p>
              </section>
            )}

            {presupuesto.notas_internas && (
              <section className="pd-card pd-card-warn">
                <h3>Notas internas</h3>
                <p className="pd-text">{presupuesto.notas_internas}</p>
              </section>
            )}

            {(generados.suscripciones.length > 0 || generados.pedidos.length > 0) && (
              <section className="pd-card">
                <h3>🔗 Activado desde este presupuesto</h3>
                {generados.suscripciones.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4 }}>Suscripciones B2B</div>
                    {generados.suscripciones.map(s => (
                      <div key={s.id} style={{ fontSize: 13, padding: '4px 0', borderBottom: '1px dashed #E2E8F0' }}>
                        <strong>{s.alias}</strong> · ${Number(s.precio_mensual_total).toLocaleString('es-UY')}/mes · {s.estado}
                      </div>
                    ))}
                  </div>
                )}
                {generados.pedidos.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4 }}>Pedidos one-shot</div>
                    {generados.pedidos.map(p => (
                      <div key={p.id} style={{ fontSize: 13, padding: '4px 0', borderBottom: '1px dashed #E2E8F0' }}>
                        <strong>{p.numero_orden}</strong> · ${Number(p.monto_total).toLocaleString('es-UY')} · {p.estado}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {presupuesto.estado === 'aceptado' && generados.suscripciones.length === 0 && generados.pedidos.length === 0 && (
              <section className="pd-card pd-card-warn">
                <h3>📋 Próximo paso</h3>
                <p className="pd-text">
                  El presupuesto está aceptado. Para {presupuesto.modalidad === 'compra_unica' ? 'crear el pedido one-shot' : 'activar el servicio mensual'} con los items y precios de este presupuesto, clickeá <strong>"{presupuesto.modalidad === 'compra_unica' ? '➕ Crear pedido B2B' : '➕ Activar suscripción B2B'}"</strong> arriba.
                </p>
              </section>
            )}

            <section className="pd-card">
              <h3>Estado del envío</h3>
              <div className="pd-timeline">
                <TimelineRow label="Creado" valor={formatFecha(presupuesto.created_at)} />
                <TimelineRow label="Enviado" valor={presupuesto.enviado_at ? `${formatFecha(presupuesto.enviado_at)}${presupuesto.enviado_a_email ? ` · ${presupuesto.enviado_a_email}` : ''}` : null} />
                <TimelineRow label="Aceptado" valor={presupuesto.aceptado_at ? `${formatFecha(presupuesto.aceptado_at)}${presupuesto.aceptado_por ? ` · ${presupuesto.aceptado_por}` : ''}` : null} />
                <TimelineRow label="Rechazado" valor={presupuesto.rechazado_at ? `${formatFecha(presupuesto.rechazado_at)}${presupuesto.rechazado_motivo ? ` · ${presupuesto.rechazado_motivo}` : ''}` : null} />
              </div>
            </section>
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}

function TimelineRow({ label, valor }) {
  return (
    <div className={`pd-tl-row${valor ? ' done' : ''}`}>
      <span className="pd-tl-label">{label}</span>
      <span className="pd-tl-valor">{valor || '—'}</span>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="pd-field">
      <span>{label}</span>
      {children}
    </div>
  )
}

function formatFecha(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

// Modal "Preparar email": plantilla pre-llenada con datos del presupuesto.
// 3 acciones: Abrir Gmail / Abrir cliente default (mailto) / Copiar al portapapeles.
// Al usar cualquiera, ofrece marcar el presupuesto como enviado.
function ModalEmail({ presupuesto, onClose, onMarcadoEnviado }) {
  const empresa = presupuesto.empresa || {}
  const numero = presupuesto.numero
  const total = Number(presupuesto.total || 0).toLocaleString('es-UY', { maximumFractionDigits: 2 })
  const validez = presupuesto.validez_dias || 15

  const [destinatario, setDestinatario] = useState(empresa.email_facturacion || '')
  const [asunto, setAsunto] = useState(`Presupuesto Limpack Nº ${numero} · ${empresa.razon_social}`)
  const [cuerpo, setCuerpo] = useState(
`Hola${empresa.contacto_principal_nombre ? ' ' + empresa.contacto_principal_nombre.split(' ')[0] : ''},

Te paso el presupuesto Nº ${numero} con todo lo que charlamos.

Resumen rápido:
${presupuesto.ocultar_total ? '• Lista de precios adjunta (precios por unidad)' : '• Total: $' + total + ' (IVA incluido)'}
• Validez: ${validez} días desde hoy
• Sin permanencia mínima

Te adjunto el PDF con el detalle. Cualquier ajuste me decís y lo armamos.

Para confirmar, respondé este mail con "Acepto" y coordinamos la primera entrega.

Saludos,
${presupuesto.creador?.nombre || 'Equipo Limpack'}
Limpack SAS · limpack.uy
+598 98 610 001`
  )
  const [copiado, setCopiado] = useState(false)

  const enc = encodeURIComponent
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${enc(destinatario)}&su=${enc(asunto)}&body=${enc(cuerpo)}`
  const mailtoUrl = `mailto:${enc(destinatario)}?subject=${enc(asunto)}&body=${enc(cuerpo)}`

  async function copiar() {
    try {
      await navigator.clipboard.writeText(`Para: ${destinatario}\nAsunto: ${asunto}\n\n${cuerpo}`)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch (_err) {
      alert('No se pudo copiar al portapapeles')
    }
  }

  async function marcarEnviadoYCerrar() {
    if (!confirm(`Marcar presupuesto Nº ${numero} como enviado a ${destinatario || '(sin email)'}?`)) return
    const patch = {
      estado: 'enviado',
      enviado_at: new Date().toISOString(),
      enviado_a_email: destinatario || null,
    }
    const { error: e } = await supabase.from('presupuestos').update(patch).eq('id', presupuesto.id)
    if (e) alert('Error: ' + e.message)
    else onMarcadoEnviado()
  }

  return (
    <div className="pd-modal-bg" onClick={onClose}>
      <div className="pd-modal" onClick={ev => ev.stopPropagation()}>
        <header className="pd-modal-head">
          <h3>📧 Preparar email</h3>
          <button className="pd-modal-close" onClick={onClose}>✕</button>
        </header>

        <div className="pd-modal-body">
          <label className="pd-field-modal">
            <span>Para</span>
            <input value={destinatario} onChange={e => setDestinatario(e.target.value)} placeholder="contacto@empresa.com" />
          </label>
          <label className="pd-field-modal">
            <span>Asunto</span>
            <input value={asunto} onChange={e => setAsunto(e.target.value)} />
          </label>
          <label className="pd-field-modal">
            <span>Cuerpo</span>
            <textarea rows={11} value={cuerpo} onChange={e => setCuerpo(e.target.value)} />
          </label>

          <div className="pd-modal-hint">
            <strong>Antes de mandarlo:</strong> generá el PDF con "📥 Imprimir / Guardar PDF" y adjuntalo al mail.
          </div>
        </div>

        <footer className="pd-modal-foot">
          <button className="pd-btn-secondary" onClick={copiar}>
            {copiado ? '✓ Copiado' : '📋 Copiar texto'}
          </button>
          <div style={{ flex: 1 }} />
          <a className="pd-btn-secondary" href={mailtoUrl}>
            ✉ Cliente email default
          </a>
          <a className="pd-btn-primary" href={gmailUrl} target="_blank" rel="noopener noreferrer">
            📨 Abrir en Gmail
          </a>
        </footer>

        {presupuesto.estado === 'borrador' && (
          <div className="pd-modal-extra">
            Una vez mandado, podés <button className="pd-btn-link" onClick={marcarEnviadoYCerrar}>marcar como enviado</button>.
          </div>
        )}
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .pd-page { max-width: 1200px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .pd-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .pd-back:hover { color: #1A3461; }

      .pd-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 18px; }
      .pd-numero { font-size: 12px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .pd-modalidad { display: inline-block; background: #F0FDFA; color: #134E4A; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: none; letter-spacing: 0; border: 1px solid #BBF7D0; }
      .pd-head h1 { margin: 0 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .pd-meta { font-size: 13px; color: #64748B; }
      .pd-badge { display: inline-block; padding: 5px 14px; border-radius: 999px; font-size: 12.5px; font-weight: 700; }

      .pd-actions-bar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 18px; padding: 14px; background: #F8FAFC; border-radius: 10px; border: 1px solid #E2E8F0; }
      .pd-btn-primary, .pd-btn-secondary, .pd-btn-success, .pd-btn-danger {
        padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 700;
        border: none; cursor: pointer; font-family: inherit; transition: all 0.15s;
      }
      .pd-btn-primary { background: #1A3461; color: #fff; }
      .pd-btn-primary:hover { background: #1A3461; }
      .pd-btn-primary:disabled { opacity: 0.5; cursor: wait; }
      .pd-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .pd-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }
      .pd-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
      .pd-btn-success { background: #16A34A; color: #fff; }
      .pd-btn-success:hover { background: #15803D; }
      .pd-btn-danger { background: #fff; color: #DC2626; border: 1.5px solid #FEE2E2; }
      .pd-btn-danger:hover { background: #FEE2E2; }

      .pd-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 18px; }
      @media (max-width: 900px) { .pd-grid { grid-template-columns: 1fr; } }

      .pd-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 18px 20px; margin-bottom: 16px; }
      .pd-card h3 { margin: 0 0 12px; font-size: 13px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; }
      .pd-card-warn { background: #FEF3C7; border-color: #F59E0B; }
      .pd-card-warn h3 { color: #78350F; }

      .pd-items { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      .pd-items th { background: #F8FAFC; padding: 9px 10px; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; text-align: left; border-bottom: 1px solid #E2E8F0; }
      .pd-items td { padding: 10px; border-bottom: 1px solid #F1F5F9; font-size: 13px; }
      .pd-items tr:last-child td { border-bottom: none; }
      .pd-num { text-align: right; font-variant-numeric: tabular-nums; }

      .pd-totales { background: #1A3461; color: #fff; padding: 12px 16px; border-radius: 8px; text-align: right; font-size: 13px; line-height: 2; }
      .pd-total { font-size: 15px; font-weight: 800; padding-top: 6px; margin-top: 6px; border-top: 1px solid rgba(255,255,255,0.15); }

      .pd-aside { display: flex; flex-direction: column; }

      .pd-field { margin-bottom: 10px; font-size: 13.5px; }
      .pd-field > span { display: block; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px; }

      .pd-text { margin: 0; font-size: 13px; line-height: 1.5; color: #1A3461; white-space: pre-line; }

      .pd-timeline { display: flex; flex-direction: column; gap: 6px; }
      .pd-tl-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #E2E8F0; font-size: 12.5px; color: #64748B; }
      .pd-tl-row:last-child { border-bottom: none; }
      .pd-tl-row.done .pd-tl-valor { color: #1A3461; font-weight: 600; }
      .pd-tl-label { font-weight: 700; color: #475569; }

      .pd-info { background: #DCFCE7; border-left: 3px solid #16A34A; color: #14532D; padding: 10px 14px; border-radius: 0 8px 8px 0; font-size: 13px; margin-bottom: 12px; }
      .pd-error { background: #FEE2E2; border-left: 3px solid #DC2626; color: #991B1B; padding: 10px 14px; border-radius: 0 8px 8px 0; font-size: 13px; margin-bottom: 12px; }

      /* Modal Preparar Email */
      .pd-modal-bg { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 16px; }
      .pd-modal { background: #fff; border-radius: 14px; max-width: 640px; width: 100%; max-height: 92vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 20px 40px rgba(15,23,42,0.2); }
      .pd-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; border-bottom: 1px solid #E2E8F0; }
      .pd-modal-head h3 { margin: 0; font-size: 16px; font-weight: 800; color: #1A3461; }
      .pd-modal-close { background: transparent; border: none; font-size: 18px; cursor: pointer; color: #64748B; padding: 4px 8px; }
      .pd-modal-close:hover { color: #1A3461; }
      .pd-modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
      .pd-modal-foot { display: flex; justify-content: flex-end; align-items: center; gap: 8px; padding: 12px 18px; border-top: 1px solid #E2E8F0; background: #F8FAFC; }
      .pd-modal-extra { padding: 10px 18px; border-top: 1px solid #E2E8F0; background: #FEF3C7; font-size: 12px; color: #78350F; text-align: center; }
      .pd-field-modal { display: block; margin-bottom: 12px; }
      .pd-field-modal > span { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      .pd-field-modal input, .pd-field-modal textarea {
        width: 100%; padding: 8px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box;
      }
      .pd-field-modal textarea { resize: vertical; min-height: 200px; line-height: 1.55; }
      .pd-field-modal input:focus, .pd-field-modal textarea:focus { outline: none; border-color: #00A8B5; }
      .pd-modal-hint { background: #F0FDFA; border-left: 3px solid #00A8B5; padding: 8px 12px; border-radius: 0 6px 6px 0; font-size: 12px; color: #134E4A; margin-top: 6px; }
      .pd-modal-hint strong { color: #1A3461; }
      .pd-btn-link { background: transparent; border: none; color: #00A8B5; cursor: pointer; font-family: inherit; font-size: 12px; padding: 0; text-decoration: underline; }
      .pd-modal-foot a.pd-btn-primary, .pd-modal-foot a.pd-btn-secondary { text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
    `}</style>
  )
}
